/**
 * API utility functions for making HTTP requests
 * Provides a consistent interface for all API calls across the application
 */

export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.prostay.com';
export const PUBLIC_SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000';

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint (e.g., '/Website/request-demo')
 * @param {Object} options - Fetch options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body] - Request body (will be JSON stringified)
 * @param {Object} [options.headers] - Additional headers
 * @param {number} [options.timeout=30000] - Request timeout in milliseconds
 * @param {boolean} [options.includeStatus=false] - Whether to return {data, status} object
 * @returns {Promise<Object>} Response data or {data, status} if includeStatus is true
 * @throws {Error} If request fails
 */
export async function apiFetch(endpoint, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    timeout = 30000,
    includeStatus = false,
    ...restOptions
  } = options;
  
  const url = endpoint.startsWith('http') ? endpoint : `${PUBLIC_SITE_URL}${endpoint}`;
  
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...restOptions,
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        errorData.message || errorData.error || `HTTP error! status: ${response.status}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = errorData;
      throw error;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Return with status if requested
    if (includeStatus) {
      return {
        data,
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      };
    }

    return data;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }

    throw error;
  }
}

/**
 * GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiGet(endpoint, options = {}) {
  return apiFetch(endpoint, { ...options, method: 'GET' });
}

/**
 * POST request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiPost(endpoint, body, options = {}) {
  return apiFetch(endpoint, { ...options, method: 'POST', body });
}

/**
 * PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiPut(endpoint, body, options = {}) {
  return apiFetch(endpoint, { ...options, method: 'PUT', body });
}

/**
 * DELETE request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiDelete(endpoint, options = {}) {
  return apiFetch(endpoint, { ...options, method: 'DELETE' });
}



